// The WP8 audio engine (§13, §25). One AudioContext, created and resumed on the first gesture;
// everything before that (and while muted) still runs the directors, so captions, state and the
// excitement-driven crowd logic are ready the moment sound starts. After unlock the pre-renderer
// renders every sound off the main thread (essential SFX first), the 25 ms scheduler keeps the
// score 120 ms ahead, and each frame feeds the music clock, the mood, loops and panning.
import type { MapId, ModeId } from '@shared/ids';
import { MapId as Maps, RoomPhase } from '@shared/ids';
import { NO_SLOT } from '@shared/constants';
import type { Bus } from '../core/bus';
import { onSim } from '../core/bus';
import type { FrameView } from '../game/FrameView';
import type { Settings } from '../profile/settings';
import { AudioDirector, type DirectorOut } from './AudioDirector';
import type { AudioEngine, UiSound } from './AudioEngine';
import { Captions } from './captions';
import { CrowdDirector } from './crowd/CrowdDirector';
import { Mixer } from './mixer';
import { DRUMS, INSTRUMENTS } from './music/instruments';
import { MusicDirector, songRequirements } from './music/MusicDirector';
import { drumKey, instKey, instrumentRefs, Sampler } from './music/sampler';
import { songForMap, SONG_IDS, type SongId } from './music/songs/index';
import { midiToFreq } from './music/theory';
import { SfxPlayer, Spatial, type PlayOpts, type Voice } from './player';
import { PreRenderer, type PreRenderStats, type RenderJob, type SoundStore } from './prerender';
import { hashId } from './sfx/recipes';
import { HONKS, SCREAMS, SOUNDS, soundDef, type SoundKey } from './sounds';

export type Projector = (x: number, y: number, z: number, out: { x: number; y: number; visible: boolean }) => void;

export interface EngineOptions {
  /** The typed bus (§23.3): sim events, replays, gamepad unlock. */
  bus?: Bus;
  /** Sim coords → CSS px (Renderer.worldToScreen), for §13.1 panning. */
  project?: Projector;
}

export interface SyncEntry {
  cue: string;
  tick: number;
  /** Context time the cue was scheduled at. */
  ctxTime: number;
  /**
   * Where the tick actually sounded on the audio clock: a linear fit of the frames' (round tick,
   * context time heard at that frame) within ±0.5 s of the tick (filled once frames pass it).
   */
  tickCtx: number;
  /** ctxTime − tickCtx, ms (§29.4: within ±15 ms). */
  errorMs: number;
  /** setRound serial the cue belongs to (frames of other rounds never fit it). */
  round: number;
  /** performance.now() and the shown round tick when the cue was committed. */
  atPerf: number;
  atTick: number;
}

export interface EngineDebug {
  unlocked(): boolean;
  state(): string;
  prerender(): PreRenderStats | null;
  sync(): SyncEntry[];
  /**
   * Main-thread audio cost per frame: mean and max since the last reset, and over the last 600
   * frames the median, p90 and the mean without the top 1 % (on a loaded host a frame where the
   * thread was descheduled mid-measurement counts hundreds of milliseconds that are not audio work).
   */
  perf(): { frames: number; avgMs: number; maxMs: number; p50Ms: number; p90Ms: number; trimmedMs: number; eventMs: number; intervalMs: number };
  music(): MusicDirector['state'] | null;
  voices(): { sfx: number; voice: number; crowd: number; ui: number; notes: number };
  peakDb(): number;
  /** Gain reduction of the bus compressor and the limiter now, dB. */
  reduction(): { comp: number; limiter: number };
  resetPerf(): void;
  /** Recent frames: [perf ms, round tick, context time heard] (sync diagnostics). */
  frames(): [number, number, number][];
  /**
   * Records the master output for `seconds` (verification: real-round mixes are analysed offline).
   * Stereo int16, base64. Null before unlock.
   */
  record(seconds: number): Promise<{ sr: number; l: string; r: string } | null>;
}

const RECORDER_JS = `class R extends AudioWorkletProcessor{process(i){const c=i[0];if(c&&c.length)this.port.postMessage([c[0].slice(),(c[1]||c[0]).slice()]);return true}}registerProcessor('shoveoff-rec',R);`;

function toBase64Int16(chunks: Float32Array[], n: number): string {
  const i16 = new Int16Array(n);
  let o = 0;
  for (const c of chunks) for (let i = 0; i < c.length && o < n; i++) i16[o++] = Math.max(-32768, Math.min(32767, Math.round(c[i] * 32767)));
  const bytes = new Uint8Array(i16.buffer);
  let s = '';
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(s);
}

const SCHEDULER_MS = 25;
/** Main-thread time per task while the essentials' render graphs are built after unlock. */
const ESSENTIAL_SLICE_MS = 4;

/** Yields to the event loop (a frame can render) and resumes in a fresh task. */
function nextTask(): Promise<void> {
  if (typeof MessageChannel === 'undefined') return new Promise((resolve) => setTimeout(resolve, 0));
  return new Promise((resolve) => {
    const ch = new MessageChannel();
    ch.port1.onmessage = () => {
      ch.port1.close();
      resolve();
    };
    ch.port2.postMessage(0);
  });
}
const PUMP_PER_FRAME = 4;
/** Per-frame pre-render share: starts and main-thread budget (ms). */
const PUMP_FRAME_STARTS = 2;
const PUMP_FRAME_BUDGET_MS = 2;
const FRAME_HISTORY = 240;
const SYNC_MAX = 96;
/** Frames within this many ticks of a cue fit its true audio time (±0.5 s). */
const SYNC_FIT_TICKS = 30;

/** A seed per key (stable variants). */
function seedOf(key: string): number {
  return hashId(key);
}

export class Engine implements AudioEngine {
  readonly captionStore = new Captions();
  private ctx: AudioContext | null = null;
  private mixer: Mixer | null = null;
  private store: SoundStore = new Map();
  private pre: PreRenderer | null = null;
  private player: SfxPlayer | null = null;
  private sampler: Sampler | null = null;
  private music: MusicDirector | null = null;
  private readonly spatial: Spatial;
  private readonly crowd: CrowdDirector;
  private readonly director: AudioDirector;
  private volumes: Settings['audio'] = { master: 80, music: 70, sfx: 80, voices: 80, crowd: 70, muteWhenHidden: true };
  private menu = false;
  private round: { mapId: MapId; modeId: ModeId; golden: boolean; roundIndex: number; startRoomTick: number } | null = null;
  private isUnlocked = false;
  private unlocking: Promise<void> | null = null;
  private interval: ReturnType<typeof setInterval> | null = null;
  private lastView: FrameView | null = null;
  private lastFrameAt = 0;
  private warned = 0;
  /** Recent non-replay frames: when (perf), which round tick, and the context time heard then. */
  private readonly frames: { perf: number; tick: number; ctx: number }[] = [];
  private readonly syncLog: SyncEntry[] = [];
  // CPU accounting (§29.4: < 1.5 ms per frame).
  private perfFrames = 0;
  private perfTotal = 0;
  private perfMax = 0;
  private perfEvents = 0;
  private perfInterval = 0;
  private frameAcc = 0;
  private readonly frameCosts = new Float32Array(600);
  private frameCostN = 0;
  private requestedSongs = new Set<SongId>();
  private deferred: { fn: () => void; key: SoundKey; until: number }[] = [];
  private roundSerial = 0;
  private recorderReady: Promise<void> | null = null;

  constructor(opts: EngineOptions = {}) {
    this.spatial = new Spatial(opts.project ?? null, () => (typeof window !== 'undefined' ? window.innerWidth : 1));
    const out = this.directorOut();
    this.crowd = new CrowdDirector(() => this.player, () => performance.now() / 1000, (t) => this.captionStore.push(t, performance.now()));
    this.director = new AudioDirector(out);
    const bus = opts.bus;
    if (bus) {
      onSim(bus, (ev, local, replay) => {
        const t0 = performance.now();
        this.guard('event', () => this.director.onSim(ev, local, replay));
        const ms = performance.now() - t0;
        this.perfEvents += ms;
        this.frameAcc += ms;
      });
      bus.on('replay:start', ({ kind }) => this.guard('event', () => {
        this.music?.replayStart(kind);
        if (kind === 'bonkcam') this.player?.play('vhsRewind', { pan: 0 });
      }));
      bus.on('replay:end', () => this.guard('event', () => this.music?.replayEnd()));
      // §13.3 victory: the match is decided (AWARDS begins).
      bus.on('match:end', () => this.guard('event', () => {
        this.music?.stinger('victory', 4);
        this.crowd.react('crowd.roar');
      }));
      // §13.1: a gamepad button also counts as the unlocking gesture.
      bus.on('input:device', (d) => {
        if (d === 'pad' && !this.isUnlocked) void this.unlock().catch(() => undefined);
      });
    }
    if (typeof document !== 'undefined') document.addEventListener('visibilitychange', () => this.onVisibility());
    if (typeof window !== 'undefined') (window as unknown as { __SHOVEOFF_AUDIO__: EngineDebug }).__SHOVEOFF_AUDIO__ = this.debug;
  }

  // ---- §25 contract ---------------------------------------------------------------------------------

  get unlocked(): boolean {
    return this.isUnlocked;
  }

  get captions(): { text: string; until: number }[] {
    return this.captionStore.list;
  }

  unlock(): Promise<void> {
    if (this.isUnlocked) return Promise.resolve();
    if (this.ctx) {
      // A context created without a gesture (gamepad) waits for this one.
      return this.ctx.resume().then(() => this.markUnlocked());
    }
    if (this.unlocking) return this.unlocking;
    this.unlocking = this.start();
    return this.unlocking;
  }

  setVolumes(v: Settings['audio']): void {
    this.volumes = { ...v };
    this.mixer?.setVolumes(this.volumes);
  }

  setRound(info: { mapId: MapId; modeId: ModeId; golden: boolean; roundIndex: number; startRoomTick: number }): void {
    this.round = { ...info };
    // A new round restarts the tick timeline: older frames would corrupt the sync fits.
    this.roundSerial++;
    this.frames.length = 0;
    this.music?.setRound(info);
    this.mixer?.setReverb(info.mapId);
    this.requestSong(songForMap(info.mapId), true);
  }

  setMenu(on: boolean): void {
    this.menu = on;
    this.music?.setMenu(on);
    this.director.setMenu(on);
    if (this.player) this.player.ambientScale = on ? 0.35 : 1;
    if (on) this.mixer?.setReverb(Maps.LOBBY);
  }

  update(view: FrameView): void {
    const t0 = performance.now();
    this.guard('update', () => this.frame(view));
    const ms = performance.now() - t0 + this.frameAcc;
    this.frameAcc = 0;
    this.perfFrames++;
    this.perfTotal += ms;
    this.perfMax = Math.max(this.perfMax, ms);
    this.frameCosts[this.frameCostN++ % this.frameCosts.length] = ms;
  }

  private sortedCosts(): number[] {
    const n = Math.min(this.frameCostN, this.frameCosts.length);
    return Array.from(this.frameCosts.subarray(0, n)).sort((a, b) => a - b);
  }

  private costQuantile(q: number): number {
    const sorted = this.sortedCosts();
    return sorted.length > 0 ? sorted[Math.min(sorted.length - 1, Math.floor(q * sorted.length))] : 0;
  }

  private trimmedCost(): number {
    const sorted = this.sortedCosts();
    const keep = sorted.slice(0, Math.max(1, Math.ceil(sorted.length * 0.99)));
    return sorted.length > 0 ? keep.reduce((a, b) => a + b, 0) / keep.length : 0;
  }

  playUi(id: UiSound): void {
    this.guard('event', () => {
      // Captions show at once, sound or not; the sound itself may wait for its render.
      this.caption(id);
      const play = (): void => {
        if (id === 'coin') this.director.coin({ pan: 0, caption: false });
        else this.play(id, { pan: 0, caption: false });
      };
      if (this.ready(id)) play();
      else this.defer(play, id);
    });
  }

  preview(kind: 'honk' | 'scream', id: number): void {
    this.guard('event', () => {
      const key = kind === 'honk' ? HONKS[id] : SCREAMS[id];
      if (!key) return;
      this.caption(key);
      const play = (): void => {
        this.play(key, { pan: 0, near: true, exact: true, caption: false });
      };
      if (this.ready(key)) play();
      else this.defer(play, key);
    });
  }

  private caption(key: SoundKey): void {
    const c = SOUNDS.get(key)?.caption;
    if (c) this.captionStore.push(c, performance.now());
  }

  private ready(key: SoundKey): boolean {
    return !!this.player?.ready(key);
  }

  /**
   * The gesture that unlocks audio is usually a button press whose own sound is not rendered yet:
   * UI sounds and previews asked for in that moment wait (≤ 1.5 s) and play once ready.
   */
  private defer(fn: () => void, key: SoundKey): void {
    if (!this.unlocking && !this.ctx) return;
    this.deferred = this.deferred.filter((d) => d.key !== key);
    this.deferred.push({ fn, key, until: performance.now() + 1500 });
  }

  private flushDeferred(): void {
    if (this.deferred.length === 0) return;
    const now = performance.now();
    const due = this.deferred;
    this.deferred = [];
    for (const d of due) {
      if (d.until < now) continue;
      if (this.ready(d.key)) d.fn();
      else this.deferred.push(d);
    }
  }

  // ---- Unlock and pre-render --------------------------------------------------------------------

  private async start(): Promise<void> {
    const Ctor = typeof window !== 'undefined' ? (window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext) : undefined;
    if (!Ctor) {
      this.markUnlocked();
      return;
    }
    const unlockAt = performance.now();
    const ctx = new Ctor({ latencyHint: 'interactive' });
    this.ctx = ctx;
    // Older iOS Safari only fully unlocks a context that plays something inside the gesture:
    // one silent sample, started synchronously here.
    try {
      const blip = ctx.createBufferSource();
      blip.buffer = ctx.createBuffer(1, 1, ctx.sampleRate);
      blip.connect(ctx.destination);
      blip.start(0);
      blip.onended = () => blip.disconnect();
    } catch {
      // Not needed where it fails.
    }
    ctx.onstatechange = () => {
      if (ctx.state === 'running') this.markUnlocked();
    };
    if (ctx.state === 'running') this.markUnlocked();
    // resume() must run inside the gesture. Everything else is built in later tasks, a few ms
    // each, so the player's first input (often a movement key) never waits on ~100 ms of setup.
    const resumed = ctx.resume().catch(() => undefined);
    await nextTask();
    const mixer = new Mixer(ctx);
    mixer.setVolumes(this.volumes);
    this.mixer = mixer;
    this.pre = new PreRenderer(ctx.sampleRate, this.store, undefined, unlockAt);
    this.player = new SfxPlayer(ctx, mixer, this.store, this.spatial);
    this.player.ambientScale = this.menu ? 0.35 : 1;
    this.sampler = new Sampler(ctx, this.store);
    this.music = new MusicDirector(ctx, mixer, this.sampler, {
      sfx: (key, when, o) => this.play(key as SoundKey, { when, pan: 0, caption: key.startsWith('ann.'), ...o }),
      caption: (text, sec) => this.captionStore.push(text, performance.now(), sec),
      synced: (cue, tick, when) => this.logSync(cue, tick, when),
    });
    this.music.setMenu(this.menu);
    if (this.round) this.music.setRound(this.round);
    this.enqueueAll();
    this.interval = setInterval(() => this.tick(), SCHEDULER_MS);
    // The essentials (bonk, whoosh, sploosh, UI) start in ≤ 4 ms slices, one task each.
    while ((this.pre as PreRenderer).startEssentials(ESSENTIAL_SLICE_MS)) await nextTask();
    // Then the room's reverb (until then the reverb send is silent).
    await nextTask();
    mixer.setReverb(this.menu || !this.round ? Maps.LOBBY : this.round.mapId);
    await resumed;
    this.markUnlocked();
  }

  private markUnlocked(): void {
    this.isUnlocked = true;
  }

  private soundJob(key: SoundKey): RenderJob {
    const d = soundDef(key);
    return { key, dur: d.recipe.dur, variants: d.variants, seed: seedOf(key), tier: d.tier, render: (k) => d.recipe.render(k), post: { db: d.db, loop: d.loop } };
  }

  private enqueueAll(): void {
    const pre = this.pre as PreRenderer;
    // Tier 0 (essential: bonk and its tone, whoosh, sploosh, UI) first, then the song about to play.
    for (const [key, d] of SOUNDS) if (d.tier === 0) pre.add(this.soundJob(key));
    const tone = INSTRUMENTS.bonkTone;
    for (const ref of instrumentRefs('bonkTone')) {
      const key = instKey('bonkTone', ref);
      pre.add({ key, dur: tone.dur, variants: 1, seed: seedOf(key), tier: 0, render: (k) => tone.render(k, midiToFreq(ref)), post: { db: 0 } });
    }
    const first: SongId = this.menu || !this.round ? 'lounge' : songForMap(this.round.mapId);
    this.requestSong(first, true);
    for (const [key, d] of SOUNDS) if (d.tier !== 0) pre.add(this.soundJob(key));
    for (const id of SONG_IDS) this.requestSong(id, false);
  }

  /** Queues a song's instruments and drums; `urgent` puts them right after the essentials. */
  private requestSong(id: SongId, urgent: boolean): void {
    const pre = this.pre;
    if (!pre) return;
    const req = songRequirements(id);
    const keys = new Set<string>();
    for (const inst of req.instruments) {
      const def = INSTRUMENTS[inst];
      for (const ref of instrumentRefs(inst)) {
        const key = instKey(inst, ref);
        keys.add(key);
        pre.add({ key, dur: def.dur, variants: 1, seed: seedOf(key), tier: urgent ? 1 : 3, render: (k) => def.render(k, midiToFreq(ref)), post: { db: 0 } });
      }
    }
    for (const drum of req.drums) {
      const key = drumKey(drum);
      keys.add(key);
      pre.add({ key, dur: DRUMS[drum].dur, variants: 2, seed: seedOf(key), tier: urgent ? 1 : 3, render: (k) => DRUMS[drum].render(k), post: { db: 0 } });
    }
    if (urgent) pre.promote(keys);
    this.requestedSongs.add(id);
  }

  // ---- Frame, scheduler ---------------------------------------------------------------------------

  private frame(view: FrameView): void {
    this.lastView = view;
    this.lastFrameAt = performance.now();
    this.captionStore.prune(view.now);
    const ctx = this.ctx;
    const heard = ctx ? this.ctxAt(view.now) : 0;
    if (view.source !== 'replay' && ctx) {
      this.frames.push({ perf: view.now, tick: view.roundTick, ctx: heard });
      if (this.frames.length > FRAME_HISTORY) this.frames.shift();
      this.resolveSync();
    }
    this.cameraTarget(view);
    if (ctx && this.music && this.player) {
      this.player.rateScale = view.source === 'replay' ? Math.max(0.6, Math.min(1, Math.sqrt(view.timeScale))) : 1;
      this.music.update(view, heard);
      this.music.schedule();
      this.pre?.pump(PUMP_FRAME_STARTS, PUMP_FRAME_BUDGET_MS);
    }
    this.crowd.update(view, this.menu);
    this.director.update(view);
  }

  private tick(): void {
    const t0 = performance.now();
    this.guard('interval', () => {
      this.music?.schedule();
      this.mixer?.update();
      if (this.ctx) this.director.flushCues();
      this.flushDeferred();
      // Keep rendering when frames stop (a background tab).
      if (performance.now() - this.lastFrameAt > 100) this.pre?.pump(PUMP_PER_FRAME, 4);
    });
    const ms = performance.now() - t0;
    this.perfInterval += ms;
    this.frameAcc += ms;
  }

  /** The camera target for distance falloff: the focus bean, else the centroid of the living. */
  private cameraTarget(view: FrameView): void {
    let x = 0;
    let y = 0;
    let n = 0;
    const focus = view.focusSlot !== NO_SLOT ? view.beans[view.focusSlot] : undefined;
    if (focus?.active && !focus.out) {
      x = focus.x;
      y = focus.y;
      n = 1;
    } else {
      for (const b of view.beans) {
        if (!b.active || b.out || b.ghost) continue;
        x += b.x;
        y += b.y;
        n++;
      }
    }
    if (n === 0) return;
    const k = 1 - Math.exp(-Math.max(0, view.dt) / 0.25);
    this.spatial.tx += (x / n - this.spatial.tx) * k;
    this.spatial.ty += (y / n - this.spatial.ty) * k;
  }

  /** Context time heard at performance time `perf` (the output timestamp, else latency estimate). */
  ctxAt(perf: number): number {
    const ctx = this.ctx;
    if (!ctx) return 0;
    const ts = typeof ctx.getOutputTimestamp === 'function' ? ctx.getOutputTimestamp() : null;
    if (ts && (ts.contextTime ?? 0) > 0 && (ts.performanceTime ?? 0) > 0) return (ts.contextTime as number) + (perf - (ts.performanceTime as number)) / 1000;
    const latency = (ctx.outputLatency || 0) + (ctx.baseLatency || 0);
    return ctx.currentTime - latency + (perf - performance.now()) / 1000;
  }

  /** performance.now() at which context time `t` is heard. */
  perfAt(t: number): number {
    const ctx = this.ctx;
    if (!ctx) return 0;
    const ts = typeof ctx.getOutputTimestamp === 'function' ? ctx.getOutputTimestamp() : null;
    if (ts && (ts.contextTime ?? 0) > 0 && (ts.performanceTime ?? 0) > 0) return (ts.performanceTime as number) + (t - (ts.contextTime as number)) * 1000;
    const latency = (ctx.outputLatency || 0) + (ctx.baseLatency || 0);
    return performance.now() + (t - ctx.currentTime + latency) * 1000;
  }

  private onVisibility(): void {
    const ctx = this.ctx;
    if (!ctx || !this.isUnlocked) return;
    if (document.visibilityState === 'hidden') {
      if (this.volumes.muteWhenHidden) void ctx.suspend().catch(() => undefined);
    } else {
      void ctx.resume().catch(() => undefined);
    }
  }

  // ---- Playback facade ----------------------------------------------------------------------------

  private play(key: SoundKey, o: PlayOpts = {}): Voice | null {
    if (o.caption !== false) {
      const c = SOUNDS.get(key)?.caption;
      if (c) this.captionStore.push(c, performance.now());
    }
    const v = this.player?.play(key, o) ?? null;
    // §13.3: the announcer ducks the music by 4 dB while it speaks.
    if (v && key.startsWith('ann.') && this.mixer && v.src.buffer) this.mixer.duck(-4, v.src.buffer.duration + 0.1, v.start);
    return v;
  }

  private directorOut(): DirectorOut {
    const self = this;
    return {
      get live() {
        return !!self.ctx && !!self.player;
      },
      play: (key, o) => this.play(key, o),
      caption: (text, sec) => this.captionStore.push(text, performance.now(), sec),
      get music() {
        return self.music;
      },
      get crowd() {
        return self.crowd;
      },
      ctxTime: () => this.ctx?.currentTime ?? 0,
      tickTime: (tick) => this.tickTime(tick),
      tickDue: (tick, horizonS) => this.music?.viewClock.anchored !== true || this.music.viewClock.due(tick, horizonS),
      cueTime: (tick) => (this.music?.viewClock.anchored ? this.music.viewClock.cueTime(tick) : this.tickTime(tick)),
      synced: (cue, tick, when) => this.logSync(cue, tick, when),
      bonkTone: (midi, when, o) => this.bonkTone(midi, when, o),
      duck: (db, sec) => this.mixer?.duck(db, sec),
      perfNow: () => performance.now(),
    };
  }

  /** Context time of a round tick: the view clock's precise mapping, else the last frame's. */
  tickTime(tick: number): number {
    const ctx = this.ctx;
    if (!ctx) return 0;
    const clock = this.music?.viewClock;
    if (clock?.anchored) return clock.preciseTime(tick);
    const v = this.lastView;
    return v ? this.ctxAt(v.now) + (tick - v.roundTick) / 60 : ctx.currentTime;
  }

  private bonkTone(midi: number, when: number, o: PlayOpts): void {
    const ctx = this.ctx;
    const mixer = this.mixer;
    if (!ctx || !mixer || !this.sampler) return;
    const g = ctx.createGain();
    const ambient = !o.near && this.player ? this.player.ambientScale : 1;
    g.gain.value = (o.gain ?? 1) * ambient * (o.x !== undefined && o.y !== undefined ? this.spatial.gain(o.x, o.y) : 1);
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = o.lowpass ?? 12000;
    const pan = ctx.createStereoPanner();
    pan.pan.value = o.pan ?? (o.x !== undefined && o.y !== undefined ? this.spatial.pan(o.x, o.y, o.z ?? 0) : 0);
    g.connect(lp).connect(pan).connect(o.ghost ? mixer.ghost : o.near ? mixer.sfxNear : mixer.sfxFar);
    const ghostDetune = o.ghost ? -30 : this.music?.current?.mood.ghost ? -15 : 0;
    this.sampler.note('bonkTone', midi, when, 0.3, 0.9, g, ghostDetune);
    setTimeout(() => {
      g.disconnect();
      lp.disconnect();
      pan.disconnect();
    }, Math.max(0, (when - ctx.currentTime) * 1000) + 900);
  }

  // ---- Sync log (§29.4: tick-locked cues within ±15 ms) ---------------------------------------------

  private logSync(cue: string, tick: number, ctxTime: number): void {
    this.syncLog.push({ cue, tick, ctxTime, tickCtx: Number.NaN, errorMs: Number.NaN, round: this.roundSerial, atPerf: performance.now(), atTick: this.lastView?.roundTick ?? Number.NaN });
    if (this.syncLog.length > SYNC_MAX) this.syncLog.shift();
  }

  /** Fills `tickCtx` once the frames cover ±30 ticks around an entry's tick. */
  private resolveSync(): void {
    const f = this.frames;
    if (f.length < 8) return;
    const newest = f[f.length - 1].tick;
    for (const e of this.syncLog) {
      if (Number.isFinite(e.tickCtx) || e.round !== this.roundSerial || newest < e.tick + SYNC_FIT_TICKS) continue;
      // The fit must bracket the tick: frames on both sides.
      let below = false;
      let above = false;
      for (const fr of f) {
        if (fr.tick <= e.tick - 2 && fr.tick >= e.tick - SYNC_FIT_TICKS) below = true;
        if (fr.tick >= e.tick + 2 && fr.tick <= e.tick + SYNC_FIT_TICKS) above = true;
      }
      if (!below || !above) {
        if (newest > e.tick + 4 * SYNC_FIT_TICKS) e.tickCtx = Number.POSITIVE_INFINITY;
        continue;
      }
      let n = 0;
      let sx = 0;
      let sy = 0;
      let sxx = 0;
      let sxy = 0;
      for (const fr of f) {
        if (Math.abs(fr.tick - e.tick) > SYNC_FIT_TICKS) continue;
        const x = fr.tick - e.tick;
        n++;
        sx += x;
        sy += fr.ctx;
        sxx += x * x;
        sxy += x * fr.ctx;
      }
      if (n < 6) {
        e.tickCtx = Number.POSITIVE_INFINITY;
        continue;
      }
      const den = n * sxx - sx * sx;
      const b = den !== 0 ? (n * sxy - sx * sy) / den : 1 / 60;
      const a = (sy - b * sx) / n;
      e.tickCtx = a;
      e.errorMs = (e.ctxTime - a) * 1000;
    }
  }

  // ---- Robustness and diagnostics -----------------------------------------------------------------

  private guard(where: string, fn: () => void): void {
    try {
      fn();
    } catch (err) {
      if (this.warned++ < 5) console.warn(`[audio] ${where} failed`, err);
    }
  }

  readonly debug: EngineDebug = {
    unlocked: () => this.isUnlocked,
    state: () => this.ctx?.state ?? 'none',
    prerender: () => this.pre?.stats ?? null,
    sync: () => this.syncLog.map((e) => ({ ...e })),
    perf: () => ({
      frames: this.perfFrames,
      avgMs: this.perfFrames > 0 ? this.perfTotal / this.perfFrames : 0,
      maxMs: this.perfMax,
      p50Ms: this.costQuantile(0.5),
      p90Ms: this.costQuantile(0.9),
      trimmedMs: this.trimmedCost(),
      eventMs: this.perfEvents,
      intervalMs: this.perfInterval,
    }),
    music: () => this.music?.state ?? null,
    voices: () => ({
      sfx: this.player?.count('sfx') ?? 0, voice: this.player?.count('voice') ?? 0, crowd: this.player?.count('crowd') ?? 0,
      ui: this.player?.count('ui') ?? 0, notes: this.sampler?.notes ?? 0,
    }),
    peakDb: () => {
      const a = this.mixer?.analyser;
      if (!a) return -Infinity;
      const buf = new Float32Array(a.fftSize);
      a.getFloatTimeDomainData(buf);
      let p = 0;
      for (const v of buf) p = Math.max(p, Math.abs(v));
      return p > 0 ? 20 * Math.log10(p) : -Infinity;
    },
    frames: () => this.frames.map((f) => [f.perf, f.tick, f.ctx] as [number, number, number]),
    reduction: () => this.mixer?.reduction ?? { comp: 0, limiter: 0 },
    record: async (seconds) => {
      const ctx = this.ctx;
      const mixer = this.mixer;
      if (!ctx || !mixer || typeof AudioWorkletNode === 'undefined') return null;
      if (!this.recorderReady) {
        const url = URL.createObjectURL(new Blob([RECORDER_JS], { type: 'text/javascript' }));
        this.recorderReady = ctx.audioWorklet.addModule(url).finally(() => URL.revokeObjectURL(url));
      }
      await this.recorderReady;
      const node = new AudioWorkletNode(ctx, 'shoveoff-rec', { numberOfInputs: 1, numberOfOutputs: 1, outputChannelCount: [2] });
      const sink = ctx.createGain();
      sink.gain.value = 0;
      const n = Math.ceil(seconds * ctx.sampleRate);
      const l: Float32Array[] = [];
      const r: Float32Array[] = [];
      let got = 0;
      await new Promise<void>((resolve) => {
        node.port.onmessage = (e: MessageEvent<[Float32Array, Float32Array]>) => {
          l.push(e.data[0]);
          r.push(e.data[1]);
          got += e.data[0].length;
          if (got >= n) resolve();
        };
        mixer.output.connect(node);
        node.connect(sink).connect(ctx.destination);
      });
      mixer.output.disconnect(node);
      node.disconnect();
      sink.disconnect();
      return { sr: ctx.sampleRate, l: toBase64Int16(l, n), r: toBase64Int16(r, n) };
    },
    resetPerf: () => {
      this.perfFrames = 0;
      this.perfTotal = 0;
      this.perfMax = 0;
      this.perfEvents = 0;
      this.perfInterval = 0;
      this.frameCostN = 0;
    },
  };

  /** Internals for the soundboard and tests. */
  get internals(): { ctx: AudioContext | null; mixer: Mixer | null; music: MusicDirector | null; player: SfxPlayer | null; sampler: Sampler | null; store: SoundStore; pre: PreRenderer | null; director: AudioDirector; crowd: CrowdDirector } {
    return { ctx: this.ctx, mixer: this.mixer, music: this.music, player: this.player, sampler: this.sampler, store: this.store, pre: this.pre, director: this.director, crowd: this.crowd };
  }

  /** Stops the scheduler and closes the context (tests, hot reload). */
  dispose(): void {
    if (this.interval !== null) clearInterval(this.interval);
    this.interval = null;
    this.player?.stopAll(0.02);
    void this.ctx?.close().catch(() => undefined);
  }

  /** Plays any catalogued sound now (soundboard). */
  playSound(key: SoundKey, o: PlayOpts = {}): Voice | null {
    return this.play(key, o);
  }

  get roomPhase(): RoomPhase | null {
    return this.lastView?.roomPhase ?? null;
  }
}

